import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ServiceRequestsPaginationData, ServiceRequestTask } from '../../../models/WidgetDashboard.model.component';
import { DashnoardService } from '../../services/dashnoard.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { Customer } from 'app/shared/customer.model';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { CoreLibGenericConfirmationModalComponent } from 'app/core-lib/dialogs/generic-confirmation-modal/generic-confirmation-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { RealtimeBusService } from 'app/shared/realtime/realtime-bus.service';
import { ConciergeConfigService } from 'app/concierge/concierge-config/services/concierge-config.service';
import { IConciergeSettingsData } from 'app/concierge/concierge-config/model/concierge-settings.interface';
import { UtilService } from 'app/shared/util.service';

type ServiceRequestTaskView = ServiceRequestTask & { updating?: boolean };

@Component({
  selector: 'mh-service-request-widget',
  templateUrl: './service-request-widget.component.html',
  styleUrl: './service-request-widget.component.scss',
  standalone: false,
})
export class ServiceRequestWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentCustomer: Customer;
  @Input() conciergeEnabled = true;
  private readonly dashnoardService = inject(DashnoardService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly realtimeBus = inject(RealtimeBusService);
  private readonly conciergeConfigService = inject(ConciergeConfigService);
  private readonly utilService = inject(UtilService);

  private readonly subscriptions = new Subscription();
  private readonly pageSize = 10;
  private readonly scrollThreshold = 40;
  private readonly statusFlow: Record<string, string> = {
    PENDING: 'IN_PROGRESS',
    IN_PROGRESS: 'FINISHED',
  };

  public tasks: ServiceRequestTaskView[] = [];
  public statuses: { id: number; name: string }[] = [];
  public paginationData: ServiceRequestsPaginationData = null;
  public currentPage = 0;
  public initialLoading = false;
  public loadingMore = false;
  public conciergeSettings: IConciergeSettingsData | null = null;

  ngOnInit() {
    if (!this.conciergeEnabled) return;

    this.getConciergeSettings();
    this.getStatuses();
    this.listenRealTimeServiceRequestEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.conciergeEnabled) return;

    const customerChange = changes.currentCustomer;
    if (!customerChange) return;

    const customer = customerChange.currentValue;
    if (!customer?.id) return;

    const previous = customerChange.previousValue;
    const customerIdChanged = !previous || previous.id !== customer.id;
    if (!customerChange.firstChange && !customerIdChanged) return;

    this.getTasks();
  }

  listenRealTimeServiceRequestEvents() {
    this.subscriptions.add(
      this.realtimeBus.serviceRequest$.subscribe((serviceRequest) => {
        if (serviceRequest) {
          this.getTasks();
        }
      }),
    );
  }

  async getConciergeSettings() {
    const hasStoredConciergeSettings = this.conciergeConfigService.getStoredConciergeSettings(+this.currentCustomer.id);
    if (hasStoredConciergeSettings) {
      this.conciergeSettings = hasStoredConciergeSettings;
      return;
    }

    try {
      const response = await this.conciergeConfigService.getConciergeSettings(+this.currentCustomer.id);
      if (response) {
        this.conciergeConfigService.setStoredConciergeSettings(+this.currentCustomer.id, response);
        this.conciergeSettings = response;
      }
    } catch (error) {
      console.error('Error getting concierge settings', error);
    }
  }

  async getStatuses() {
    try {
      const response = await firstValueFrom(this.dashnoardService.getServiceRequestsStatuses());

      if (response) {
        this.statuses = response.data;
      }
    } catch (error) {
      console.error('Error getting statuses', error);
    }
  }

  async getTasks(append = false) {
    if (this.loadingMore || this.initialLoading) return;

    if (append) {
      this.loadingMore = true;
    } else {
      this.initialLoading = true;
      this.currentPage = 0;
    }

    try {
      const response = await firstValueFrom(
        this.dashnoardService.getServiceRequests({
          customer_id: this.currentCustomer.id,
          page: this.currentPage,
          size: this.pageSize,
        }),
      );

      if (response) {
        const incoming = (response.data.info ?? []).map((task) => ({ ...task, updating: false }));
        this.tasks = append ? [...this.tasks, ...incoming] : incoming;
        this.paginationData = {
          page_number: response.data.page_number,
          total_pages: response.data.total_pages,
          has_next: response.data.has_next,
          total_of_elements: response.data.total_of_elements,
          finished_tasks: response.data.finished_last_30_days,
        };
      }
    } catch (error) {
      console.error('Error getting tasks', error);
    } finally {
      this.initialLoading = false;
      this.loadingMore = false;
    }
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const position = element.scrollTop + element.clientHeight;
    const height = element.scrollHeight;

    if (position >= height - this.scrollThreshold) {
      this.loadMore();
    }
  }

  loadMore(): void {
    if (this.loadingMore || this.initialLoading) return;
    if (!this.paginationData?.has_next) return;

    this.currentPage += 1;
    this.getTasks(true);
  }

  async handleActionClick(task: ServiceRequestTaskView) {
    if (task.updating) return;

    if (task.category_name === 'Talk_to_human') {
      const finishedStatus = this.statuses.find((status) => status.name === 'FINISHED');
      if (!finishedStatus) return;

      this.router.navigate(['/concierge/conversations'], {
        queryParams: {
          conversationId: task.conversation_id,
          customer_id: this.currentCustomer.id,
          take_conversation: true,
        },
      });

      await this.updateTaskStatus(task, finishedStatus.id, 'FINISHED');

      return;
    }

    const nextStatusName = this.statusFlow[task.status_name];
    if (!nextStatusName) return;

    const nextStatus = this.statuses.find((status) => status.name === nextStatusName);
    if (!nextStatus) return;

    const confirmed = this.conciergeSettings.confirm_status_change
      ? await this.confirmStatusChange(nextStatusName)
      : true;
    if (!confirmed) return;

    await this.updateTaskStatus(task, nextStatus.id, nextStatusName);
  }

  handleCardClick(task: ServiceRequestTaskView) {
    if (task) {
      const url = `${window.location.origin}/concierge/conversations?conversationId=${task.conversation_id}&customer_id=${this.currentCustomer.id}`;
      window.open(url, '_blank');
    }
  }

  private async updateTaskStatus(task: ServiceRequestTaskView, nextStatusId: number, nextStatusName: string) {
    task.updating = true;

    try {
      await firstValueFrom(this.dashnoardService.updateServiceRequestStatus(task.id, nextStatusId));
      task.status_name = nextStatusName;
    } catch (error) {
      console.error('Error updating service request status', error);
    } finally {
      task.updating = false;
    }
  }

  private confirmStatusChange(nextStatusName: string): Promise<boolean> {
    const statusLabel = this.translateService.instant(`dashboard.service_requests.${nextStatusName}`);
    const text = this.translateService.instant('dashboard.service_requests.confirm_status_change', {
      status: statusLabel,
    });

    const dialogRef = this.modalService.open(
      CoreLibGenericConfirmationModalComponent,
      {
        data: { text },
      },
      ['overlay-panel', 'sm', '!tw-h-auto'],
    );

    return new Promise<boolean>((resolve) => {
      dialogRef.afterClosed().subscribe((response) => {
        resolve(response === 'ACCEPT');
      });
    });
  }

  goToConciergePlg(): void {

    this.utilService.ga('', 'widget_clicked', null, null, {
        product: 'concierge',
        widget_name: 'guest_request',
        action: 'learn_more',
      });
    this.router.navigate(['/plg/concierge']);
  }

  goToSettings(): void {
    this.router.navigate(['/concierge/configuration'], {
      queryParams: {
        tab: 'service_requests',
      },
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
